/**
 * @fileoverview Trending topics example and country management utilities.
 * Provides functions for extracting, formatting, and managing trending topic examples
 * across different countries and languages.
 * @module helperMethods/trendingTopics/methods
 */

import _ from "lodash";
import { COUNTRY_LIST } from "./countryList";
import { ExampleRecord, ExampleTrending, OrderUpdate } from "./type";

/**
 * Gets the published example from the first country in the examples record.
 *
 * @param {string} examples - JSON string of ExampleRecord
 * @returns {string | undefined} Published example text, or empty string if none
 *
 * @example
 * getPublishedExample('{"en":{"examples":[{"example":"Hello","published":true}],"answer":""}}');
 * // Returns: "Hello"
 */
export const getPublishedExample = (examples: string): string | undefined => {
  const exampleRecord: ExampleRecord = JSON.parse(examples);
  if (_.isEmpty(exampleRecord)) return "";
  const publishedExample = exampleRecord[Object.keys(exampleRecord)[0]].examples.find(
    (example) => example.published
  );
  return publishedExample?.example;
};

/**
 * Gets the published example for a specific country code.
 * Falls back to first country's published example if country not specified.
 *
 * @param {string} examples - JSON string of ExampleRecord
 * @param {string} [countryCode] - Country code to get example for
 * @returns {string | undefined} Published example text for the country
 *
 * @example
 * getExampleByCountry('{"en":{"examples":[{"example":"Hello","published":true}]}}', 'en');
 * // Returns: "Hello"
 */
export const getExampleByCountry = (examples: string, countryCode?: string): string | undefined => {
  if (!countryCode) return getPublishedExample(examples);
  const exampleRecord: ExampleRecord = JSON.parse(examples);
  if (!exampleRecord[countryCode]) return "";
  const publishedExample = exampleRecord[countryCode].examples.find((example) => example.published);
  return publishedExample?.example;
};

/**
 * Extracts button text or data from a stringified button object.
 *
 * @param {string} data - JSON string of button object
 * @returns {string} Button text if URL exists, otherwise button data
 *
 * @example
 * getTextButton('{"text":"Click me","url":"https://example.com"}');
 * // Returns: "Click me"
 *
 * @example
 * getTextButton('{"text":"Go","data":"next_step"}');
 * // Returns: "next_step"
 */
export const getTextButton = (data: string): string => {
  const button = JSON.parse(data);
  if (button.url) return button.text;
  return button.data;
};

/**
 * Parses examples array from JSON string.
 *
 * @param {string} [exampleString] - JSON string of ExampleTrending array
 * @returns {ExampleTrending[] | []} Parsed examples array, or empty array
 *
 * @example
 * getExamplesFromString('[{"example":"Hello","published":true}]');
 * // Returns: [{ example: "Hello", published: true }]
 */
export const getExamplesFromString = (exampleString?: string): ExampleTrending[] | [] => {
  return exampleString ? JSON.parse(exampleString) : [];
};

/**
 * Parses ExampleRecord from JSON string.
 *
 * @param {string} [example] - JSON string of ExampleRecord
 * @returns {ExampleRecord | {}} Parsed example record, or empty object
 *
 * @example
 * getExampleFromString('{"en":{"examples":[],"answer":"Hi"}}');
 * // Returns: { en: { examples: [], answer: "Hi" } }
 */
export const getExampleFromString = (example?: string): ExampleRecord | {} => {
  return example ? JSON.parse(example) : {};
};

/**
 * Capitalizes the first letter of a string.
 *
 * @param {string} string - String to capitalize
 * @returns {string} String with first letter capitalized
 *
 * @example
 * capitalizeFirstLetter('hello');
 * // Returns: "Hello"
 */
export const capitalizeFirstLetter = (string: string): string => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

/**
 * Prepares an example for saving by updating or adding it to the country's examples.
 * Manages published status - only one example can be published per country.
 *
 * @param {string} countryKey - Country code for the example
 * @param {string} example - Example text to save
 * @param {string} oldExample - Existing examples JSON string
 * @returns {ExampleRecord} Updated example record
 *
 * @example
 * prepareExampleToSave('en', 'Hello World', '{}');
 * // Returns: { en: { examples: [{ example: 'Hello World', published: true }], answer: '' } }
 */
export const prepareExampleToSave = (
  countryKey: string,
  example: string,
  oldExample: string
): ExampleRecord => {
  const publishedExample: ExampleTrending = { example, published: true };
  const oldExamples: ExampleRecord = getExampleFromString(oldExample);
  if (_.isEmpty(oldExample)) {
    return {
      [countryKey]: {
        examples: [publishedExample],
        answer: "",
      },
    };
  }
  if (_.isEmpty(oldExamples[countryKey])) {
    oldExamples[countryKey] = {
      examples: [publishedExample],
      answer: "",
    };
    return oldExamples;
  }
  const checkExistExample = [...oldExamples[countryKey].examples].find(
    (eg) => eg.example.trim() === example.trim()
  );
  if (checkExistExample) {
    oldExamples[countryKey].examples = [
      ...[...oldExamples[countryKey].examples].map((eg) => ({
        ...eg,
        published: eg.example === example,
      })),
    ];
    return oldExamples;
  }
  oldExamples[countryKey].examples = [
    ...[...oldExamples[countryKey].examples].map((example) => ({ ...example, published: false })),
    publishedExample,
  ];
  return oldExamples;
};

/**
 * Updates the answer for a specific country in the example record.
 *
 * @param {string} answer - Answer text to save
 * @param {string} oldExample - Existing examples JSON string
 * @param {string} countryKey - Country code to update answer for
 * @returns {ExampleRecord} Updated example record with new answer
 *
 * @example
 * prepareAnswerToSave('Hi there!', '{"en":{"examples":[],"answer":""}}', 'en');
 * // Returns: { en: { examples: [], answer: 'Hi there!' } }
 */
export const prepareAnswerToSave = (
  answer: string,
  oldExample: string,
  countryKey: string
): ExampleRecord => {
  const oldExamples: ExampleRecord = getExampleFromString(oldExample);
  oldExamples[countryKey].answer = answer;
  return oldExamples;
};

/**
 * Gets the display name for a country from its country code.
 *
 * @param {string} countryCode - ISO 639-1 language/country code
 * @returns {string} Country/language name, or the code itself if not found
 *
 * @example
 * getNameCountryFromExample('en');
 * // Returns: "English"
 *
 * @example
 * getNameCountryFromExample('unknown');
 * // Returns: "unknown"
 */
export const getNameCountryFromExample = (countryCode: string): string => {
  const country = COUNTRY_LIST.find((country) => country.link === countryCode);
  return country ? country.value : countryCode;
};

/**
 * Calculates the height difference between two DOM elements.
 * Used for dynamic height calculations in trending topics UI.
 *
 * @param {string} coverClassName - Class name of the container element
 * @param {string} extraClassName - Class name of the element to subtract
 * @param {number} [offSet=20] - Additional offset to subtract
 * @returns {number} Calculated height difference
 *
 * @example
 * getHeightElement('trending-container', 'trending-header', 20);
 * // Returns: container height - header height - 20
 */
export const getHeightElement = (
  coverClassName: string,
  extraClassName: string,
  offSet = 20
): number => {
  const extra = document.getElementsByClassName(extraClassName) as HTMLCollectionOf<HTMLElement>;
  const cover = document.getElementsByClassName(coverClassName) as HTMLCollectionOf<HTMLElement>;
  return Math.abs(cover[0].offsetHeight - extra[0].offsetHeight - offSet);
};

/**
 * Generates a success message based on trending topic operations performed.
 * Combines messages for publishing, sorting, and removing operations.
 *
 * @param {OrderUpdate[]} dataPublishTopic - Published topics data
 * @param {OrderUpdate[]} dataUpdateOrder - Sorted topics data
 * @param {number[]} dataRemove - Removed topic IDs
 * @returns {string} Combined success message
 *
 * @example
 * returnPublishSuccessMessage([{id:1,order:1}], [{id:2,order:2}], [3]);
 * // Returns: "Trending topics published sorted removed successfull"
 */
export const returnPublishSuccessMessage = (
  dataPublishTopic: OrderUpdate[],
  dataUpdateOrder: OrderUpdate[],
  dataRemove: number[]
): string => {
  let messageSuccess = "Trending topics";
  if (dataPublishTopic.length > 0) {
    messageSuccess += " published ";
  }
  if (dataUpdateOrder.length > 0) {
    messageSuccess += " sorted ";
  }
  if (dataRemove.length > 0) {
    messageSuccess += " removed ";
  }
  messageSuccess += " successfull";
  return messageSuccess;
};

/**
 * Checks if a trending topic can be published.
 * Topics with URL buttons or existing answers cannot be published.
 *
 * @param {string} intentId - Intent ID JSON string
 * @param {string} [examples=''] - Examples JSON string
 * @returns {boolean} True if topic can be published
 *
 * @example
 * checkCanPublishTopic('{"data":"greeting"}', '{}');
 * // Returns: true
 *
 * @example
 * checkCanPublishTopic('{"url":"https://example.com"}', '');
 * // Returns: false (has URL)
 */
export const checkCanPublishTopic=(intentId:string,examples=''):boolean=>{
  if (!examples) return true;
  const exampleRecord : ExampleRecord = JSON.parse(examples);
  const { url } = JSON.parse(intentId);
  if (url) return false;
  const existAnyAnswer = Object.values(exampleRecord).find(({ answer }) => answer);
  return _.isEmpty(exampleRecord) || !existAnyAnswer;
}

/**
 * Extracts intent data and name from intent ID JSON string.
 *
 * @param {string} intentId - Intent ID JSON string
 * @returns {{data: string, intentName: string}} Object with data and intentName properties
 *
 * @example
 * getIntentName('{"data":"greeting","intentName":"Greeting"}');
 * // Returns: { data: 'greeting', intentName: 'Greeting' }
 */
export const getIntentName = (intentId:string):{data:string,intentName:string}=>{
  const {data,intentName=''}= JSON.parse(intentId)
  return {
    data,
    intentName
  }
}
